import type Luminary from '@lumen5/luminary';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { BoundingBox, VideoPlanRequest } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';

export type ClipIDType = number;
export type ClipVersionIDType = number;

export const ORIGINAL_VERSION_ID = 0;

export enum ClipVersionStatus {
    PENDING = 'PENDING',
    LOADING = 'LOADING',
    LOADED = 'LOADED',
    FAILED = 'FAILED',
}

type ClipVersionCommonData = {
    clipId: ClipIDType;
    versionId: ClipVersionIDType;
    videoPlan?: VideoPlanRequest;
    luminary?: Luminary;
};
type PendingClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.PENDING;
};
type LoadingClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.LOADING;
};
type LoadedClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.LOADED;
    luminary: Luminary;
};
type FailedClipVersionData = ClipVersionCommonData & {
    status: ClipVersionStatus.FAILED;
};
export type ClipVersionData =
    | PendingClipVersionData
    | LoadingClipVersionData
    | LoadedClipVersionData
    | FailedClipVersionData;

export type ClipVersionDataPayload = ClipVersionData;

type ClipVersionsInitialState = {
    allIds: ClipVersionIDType[];
    byId: Record<ClipVersionIDType, ClipVersionData>;
    selected: ClipVersionIDType | null;
};

export type ClipData = {
    index: number;
    title: string;
    luminary: Luminary;
    hasCuts: boolean;
    versions: ClipVersionsInitialState;
    faceDetections: BoundingBox[] | null;
    videoId?: number;
    prompt?: string;
    downloaded?: boolean;
};

type ClipsInitialState = {
    allIds: ClipIDType[];
    byId: Record<ClipIDType, ClipData>;
    selected: ClipIDType | null;
};

const initialState: ClipsInitialState = {
    allIds: [],
    byId: {},
    selected: null,
};

export const clips = createSlice({
    name: 'clips',
    initialState: initialState,
    reducers: {
        addClipAndSelectAddedClip: (
            state,
            action: PayloadAction<{
                luminary: Luminary;
                title?: string;
                prompt?: string;
            }>,
        ) => {
            const nextIndex = calculateNextIndex(state);
            const title = action.payload.title || `Clip ${nextIndex}`;
            const luminary = action.payload.luminary;
            const prompt = action.payload.prompt;
            const clipId = nextIndex;
            const data: ClipData = {
                index: nextIndex,
                title,
                prompt,
                luminary,
                hasCuts: false,
                faceDetections: null,
                versions: {
                    allIds: [ORIGINAL_VERSION_ID],
                    byId: {
                        [ORIGINAL_VERSION_ID]: {
                            clipId,
                            versionId: ORIGINAL_VERSION_ID,
                            status: ClipVersionStatus.LOADED,
                            luminary,
                        },
                    },
                    selected: ORIGINAL_VERSION_ID,
                },
            };
            state.allIds.unshift(clipId); // add new clip to the beginning of the array
            state.byId[clipId] = data;
            state.selected = clipId;
        },
        deleteClipAndSelectNextClip: (state, action: PayloadAction<ClipIDType>) => {
            let idForSelection = state.selected;
            const idForDeletion = action.payload;
            const idForDeletionIndex = state.allIds.indexOf(idForDeletion);
            if (idForDeletion === state.selected) {
                const nextIdIndex =
                    idForDeletionIndex === state.allIds.length - 1
                        ? idForDeletionIndex - 1
                        : idForDeletionIndex + 1; // id+1 is necessary because allIds has not been spliced yet
                idForSelection = nextIdIndex === -1 ? null : state.allIds[nextIdIndex];
            }

            // store reference of old luminaries
            const luminariesToDestroy: Luminary[] = [];
            state.byId[idForDeletion].versions.allIds.forEach((versionId) => {
                const version = state.byId[idForDeletion].versions.byId[versionId];
                if (version.luminary) {
                    luminariesToDestroy.push(version.luminary);
                }
            });

            // update state
            state.allIds.splice(idForDeletionIndex, 1);
            delete state.byId[idForDeletion];
            state.selected = idForSelection;

            // destroy old luminary without waiting
            luminariesToDestroy.forEach((luminary) => luminary.destroy());
        },
        setSelectedClipId: (state, action: PayloadAction<ClipIDType>) => {
            state.selected = action.payload;
        },
        setClipHasCuts: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; hasCuts: boolean }>,
        ) => {
            const { clipId, hasCuts } = action.payload;
            state.byId[clipId].hasCuts = hasCuts;
        },
        setSelectedClipHasCuts: (state, action: PayloadAction<boolean>) => {
            if (state.selected) {
                state.byId[state.selected].hasCuts = action.payload;
            }
        },
        setVideoIdForClipId: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; videoId: number }>,
        ) => {
            state.byId[action.payload.clipId].videoId = action.payload.videoId;
        },
        setClipDownloaded: (state, action: PayloadAction<{ clipId: ClipIDType }>) => {
            state.byId[action.payload.clipId].downloaded = true;
        },
        setLuminaryForClipId: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; luminary: Luminary }>,
        ) => {
            const { clipId, luminary } = action.payload;
            if (state.byId[clipId]) {
                // store reference of old luminary
                const oldLuminary = state.byId[clipId].luminary;

                // update state
                state.byId[clipId].luminary = luminary;

                // destroy old luminary without waiting
                oldLuminary.destroy();
            }
        },
        setClipTitle: (state, action: PayloadAction<{ clipId: ClipIDType; title: string }>) => {
            const { clipId, title } = action.payload;
            if (state.byId[clipId]) {
                state.byId[clipId].title = title;
            }
        },
        setClipPrompt: (state, action: PayloadAction<{ clipId: ClipIDType; prompt: string }>) => {
            const { clipId, prompt } = action.payload;
            if (state.byId[clipId]) {
                state.byId[clipId].prompt = prompt;
            }
        },
        updateClipVersionStatus: (
            state,
            action: PayloadAction<{
                clipId: ClipIDType;
                versionId: ClipVersionIDType;
                status: ClipVersionStatus;
            }>,
        ) => {
            const { clipId, versionId, status } = action.payload;
            state.byId[clipId].versions.byId[versionId].status = status;
        },
        updateClipVersionLuminary: (
            state,
            action: PayloadAction<{
                clipId: ClipIDType;
                versionId: ClipVersionIDType;
                luminary: Luminary;
            }>,
        ) => {
            const { clipId, versionId, luminary } = action.payload;
            const oldLuminary = state.byId[clipId].versions.byId[versionId].luminary;
            state.byId[clipId].versions.byId[versionId].luminary = luminary;
            if (oldLuminary) {
                oldLuminary.destroy();
            }
        },
        selectClipVersion: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; versionId: ClipVersionIDType }>,
        ) => {
            const { clipId, versionId } = action.payload;
            const clip = state.byId[clipId];
            if (!clip) {
                throw new Error(`Clip with ID ${clipId} not found`);
            }
            const version = clip.versions.byId[versionId];
            if (!version) {
                throw new Error(`Version with ID ${versionId} not found`);
            }
            if (version.status !== ClipVersionStatus.LOADED) {
                throw new Error(`Version with ID ${versionId} is not loaded`);
            }
            clip.versions.selected = versionId;
            clip.luminary = version.luminary;
        },
        addClipVersions: (state, action: PayloadAction<ClipVersionDataPayload[]>) => {
            mergeWithExistingClipVersions(state, action.payload);
        },
        deleteAllClipVersions: (state, action: PayloadAction<{ clipId: ClipIDType }>) => {
            const { clipId } = action.payload;
            const clip = state.byId[clipId];
            if (!clip) {
                throw new Error(`Clip with ID ${clipId} not found`);
            }

            const versionIdsToDelete = clip.versions.allIds.filter(
                (versionId) => versionId !== ORIGINAL_VERSION_ID,
            );
            const luminariesToDestroy = versionIdsToDelete
                .map((versionId) => {
                    const version = clip.versions.byId[versionId];
                    return version.luminary;
                })
                .filter((luminary) => luminary !== undefined);

            clip.versions.selected = ORIGINAL_VERSION_ID;
            clip.versions.allIds = [ORIGINAL_VERSION_ID];
            versionIdsToDelete.forEach((versionId) => {
                delete clip.versions.byId[versionId];
            });

            luminariesToDestroy.forEach((luminary) => luminary.destroy());
        },
        deleteEmptyClips: (state) => {
            const emptyClipIds = state.allIds.filter((clipId) => !state.byId[clipId].hasCuts);
            emptyClipIds.forEach((clipId) => {
                delete state.byId[clipId];
                state.allIds = state.allIds.filter((id) => id !== clipId);
            });
            if (state.selected && emptyClipIds.includes(state.selected)) {
                state.selected = state.allIds.length > 0 ? state.allIds[0] : null;
            }
        },
        setFaceDetectionsForClipId: (
            state,
            action: PayloadAction<{ clipId: ClipIDType; faceDetections: BoundingBox[] }>,
        ) => {
            const { clipId, faceDetections } = action.payload;
            state.byId[clipId].faceDetections = faceDetections;
        },
    },
    selectors: {
        getAllClipIds: (state) => state.allIds,
        getClipIdsWithCuts: (state) => state.allIds.filter((id) => state.byId[id].hasCuts),
        getClipById: (state, id: number) => state.byId[id],
        getCountAllClips: (state: ClipsInitialState) => {
            return state.allIds.length;
        },
        getCountClipsToSave: (state) => {
            return state.allIds.filter((id) => state.byId[id].hasCuts).length;
        },
        getCountEmptyClips: (state) => {
            return state.allIds.filter((id) => !state.byId[id].hasCuts).length;
        },
        getSelectedClipId: (state: ClipsInitialState) => state.selected,
        getSelectedClip: (state: ClipsInitialState) => {
            if (state.selected) {
                return state.byId[state.selected];
            }
            return undefined;
        },
        getClipLuminaries: createSelector(
            [(state: ClipsInitialState) => state.byId],
            (byId): Luminary[] => {
                const luminaries: Luminary[] = [];
                for (const clipData of Object.values(byId)) {
                    const clipVersions = clipData.versions.byId;
                    for (const versionData of Object.values(clipVersions)) {
                        if (versionData.status === ClipVersionStatus.LOADED) {
                            luminaries.push(versionData.luminary);
                        }
                    }
                }
                return luminaries;
            },
        ),
        getClipVersionIds: (state: ClipsInitialState, clipId: ClipIDType) => {
            const clip = state.byId[clipId];
            return clip.versions.allIds;
        },
        getClipVersionById: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const clip = state.byId[clipId];
            return clip.versions.byId[versionId];
        },
        getSelectedClipVersionId: (state: ClipsInitialState, clipId: ClipIDType) => {
            const clip = state.byId[clipId];
            return clip?.versions.selected;
        },
        isClipVersionLoaded: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const version = state.byId[clipId].versions.byId[versionId];
            return version.status === ClipVersionStatus.LOADED;
        },
        isClipVersionLoading: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const version = state.byId[clipId].versions.byId[versionId];
            return version.status === ClipVersionStatus.LOADING;
        },
        isClipVersionPending: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const version = state.byId[clipId].versions.byId[versionId];
            return version.status === ClipVersionStatus.PENDING;
        },
        isClipVersionFailed: (
            state: ClipsInitialState,
            clipId: ClipIDType,
            versionId: ClipVersionIDType,
        ) => {
            const version = state.byId[clipId].versions.byId[versionId];
            return version.status === ClipVersionStatus.FAILED;
        },
        getFaceDetectionsForClipId: (state: ClipsInitialState, clipId: ClipIDType) => {
            return state.byId[clipId].faceDetections;
        },
    },
});

const findMaxIndex = (state: ClipsInitialState) => {
    let maxIndex = 0;
    if (state.allIds.length > 0) {
        state.allIds.forEach((id) => {
            const clip = state.byId[id];
            maxIndex = Math.max(maxIndex, clip.index);
        });
    }
    return maxIndex;
};

const calculateNextIndex = (state: ClipsInitialState) => {
    const maxIndex = findMaxIndex(state);
    return maxIndex + 1;
};

const mergeWithExistingClipVersions = (
    state: ClipsInitialState,
    payload: ClipVersionDataPayload[],
) => {
    payload.forEach((payload) => {
        const { clipId, versionId, ...rest } = payload;
        const clip = state.byId[clipId];
        if (!clip) {
            throw new Error(`Clip with ID ${clipId} not found`);
        }
        if (!clip.versions.byId[versionId]) {
            clip.versions.byId[versionId] = {
                clipId,
                versionId,
                status: ClipVersionStatus.PENDING,
            };
            clip.versions.allIds.push(versionId);
        }
        clip.versions.byId[versionId] = {
            ...clip.versions.byId[versionId],
            ...rest,
        };
        validateMergedClipVersion(clip.versions.byId[versionId]);
    });
};

const validateMergedClipVersion = (clipVersionData: ClipVersionData) => {
    if (clipVersionData.status === ClipVersionStatus.LOADED && !clipVersionData.luminary) {
        throw new Error(`Version with ID ${clipVersionData.versionId} does not have luminary`);
    }
};

export const {
    addClipAndSelectAddedClip,
    deleteClipAndSelectNextClip,
    setSelectedClipId,
    setClipHasCuts,
    setSelectedClipHasCuts,
    setVideoIdForClipId,
    setClipDownloaded,
    setLuminaryForClipId,
    setClipTitle,
    setClipPrompt,
    updateClipVersionStatus,
    updateClipVersionLuminary,
    selectClipVersion,
    addClipVersions,
    deleteAllClipVersions,
    deleteEmptyClips,
    setFaceDetectionsForClipId,
} = clips.actions;

export const {
    getAllClipIds,
    getClipIdsWithCuts,
    getClipById,
    getCountAllClips,
    getCountClipsToSave,
    getCountEmptyClips,
    getSelectedClipId,
    getSelectedClip,
    getClipLuminaries,
    getClipVersionIds,
    getClipVersionById,
    getSelectedClipVersionId,
    isClipVersionPending,
    isClipVersionLoading,
    isClipVersionLoaded,
    isClipVersionFailed,
    getFaceDetectionsForClipId,
} = clips.getSelectors((state: RootState) => state.clip_creator.clips);
